import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon, PencilIcon, ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { QRCodeService } from '../utils/qrCodeService';
import { CONFIG, authHeaders, authFetch } from '../utils/config';
import { useContent } from '../hooks/useContent';
import QRCodeStats from './QRCodeStats';
import QRCodeEditorInline from './QRCodeEditorInline';

const imageCache = new Map();

const QRCodeRow = ({ qrCode, onStatsLoad, cachedStats, onEdit, showEditButton = false, onRowClick = null, onUpdate, onDownload, onDelete, expandMode = 'stats' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState(cachedStats || null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [highQualityImage, setHighQualityImage] = useState(null);
  const [isLoadingHQ, setIsLoadingHQ] = useState(false);
  const { isDark } = useTheme();
  const { content } = useContent('qrCodeRow');

  const t = content || {
    columns: { redirectUrl: 'Lien de redirection', createdAt: 'Créé le', expiresAt: 'Expire le', unlimited: 'Illimité', scans: 'Scans' },
    actions: { edit: 'Modifier le QR Code', viewStats: 'Voir les statistiques', download: 'Télécharger le QR Code', delete: 'Supprimer le QR Code', openLink: 'Ouvrir le lien' },
    thumbnailAlt: 'QR Code Miniature',
    expandedAlt: 'QR Code Agrandi',
    loadingStats: 'Chargement des statistiques...',
    errorStats: 'Erreur lors du chargement des statistiques',
  };

  const qrThumbnail = QRCodeService.getQRThumbnailUrl(qrCode);

  const loadHighQualityImage = async () => {
    if (!qrCode?.uuid || highQualityImage || isLoadingHQ) return;

    const cacheKey = `hq_${qrCode.uuid}`;
    if (imageCache.has(cacheKey)) {
      setHighQualityImage(imageCache.get(cacheKey));
      return;
    }

    setIsLoadingHQ(true);
    try {
      const params = new URLSearchParams({ miniature: 'true', miniature_quality: '90', miniature_size: '512' });
      const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/${qrCode.uuid}?${params.toString()}`, {
        method: 'GET',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const hqUrl = QRCodeService.getQRThumbnailUrl(data);
        imageCache.set(cacheKey, hqUrl);
        setHighQualityImage(hqUrl);
      }
    } catch (error) {
      console.error('Erreur chargement image HQ:', error);
    } finally {
      setIsLoadingHQ(false);
    }
  };

  const autoLoadAttemptedRef = useRef(false);

  useEffect(() => {
    if (cachedStats && !stats) setStats(cachedStats);
  }, [cachedStats]);

  useEffect(() => {
    if (!autoLoadAttemptedRef.current && !cachedStats && !stats && qrCode?.uuid) {
      autoLoadAttemptedRef.current = true;
      setIsLoadingStats(true);
      QRCodeService.getQRCodeStats(qrCode.uuid)
        .then(statsData => {
          setStats(statsData);
          onStatsLoad?.(qrCode.uuid, statsData);
        })
        .catch(err => console.error('Erreur auto-chargement stats:', err))
        .finally(() => setIsLoadingStats(false));
    }
  }, [qrCode?.uuid, cachedStats, stats]);

  const handleRowClick = () => {
    if (onRowClick) onRowClick(qrCode);
    else if (!showEditButton) handleToggleExpand();
  };

  const handleToggleExpand = async () => {
    if (expandMode === 'edit') {
      setIsEditing(!isExpanded);
      setIsExpanded(!isExpanded);
    } else {
      if (!isExpanded && !stats) {
        setIsLoadingStats(true);
        setStatsError(null);
        try {
          const statsData = await QRCodeService.getQRCodeStats(qrCode.uuid);
          setStats(statsData);
          onStatsLoad?.(qrCode.uuid, statsData);
        } catch (error) {
          setStatsError(t.errorStats);
        } finally {
          setIsLoadingStats(false);
        }
      }
      setIsExpanded(!isExpanded);
    }
  };

  const handleOpenLink = () => {
    if (qrCode.redirect_url || qrCode.url) window.open(qrCode.redirect_url || qrCode.url, '_blank');
  };

  const handleEdit = () => {
    if (onEdit) onEdit(qrCode);
    else { setIsEditing(true); setIsExpanded(true); }
  };

  const handleUpdateQRCode = (updatedQRCode) => {
    onUpdate?.(updatedQRCode);
    setIsEditing(false);
    setIsExpanded(false);
  };

  const handleCancelEdit = () => { setIsEditing(false); setIsExpanded(false); };
  const handleDownload = (e) => { e.stopPropagation(); onDownload?.(qrCode); };
  const handleDelete = (e) => { e.stopPropagation(); onDelete?.(qrCode); };

  return (
    <div className={`border rounded-lg ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      <div
        className={`p-4 transition-colors ${(onRowClick || !showEditButton) ? `cursor-pointer ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-50'}` : ''}`}
        onClick={handleRowClick}
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:space-x-4 flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {onRowClick || showEditButton ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}
                    className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-dark-600' : 'hover:bg-gray-100'}`}
                    title={expandMode === 'edit' ? t.actions.edit : t.actions.viewStats}
                  >
                    {isExpanded
                      ? <ChevronDownIcon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      : <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    }
                  </button>
                ) : (
                  <>
                    {isExpanded
                      ? <ChevronDownIcon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      : <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    }
                  </>
                )}
              </div>

              <div className="flex-shrink-0 relative group/thumbnail" onMouseEnter={loadHighQualityImage}>
                {qrThumbnail && !thumbnailError ? (
                  <>
                    <img
                      src={qrThumbnail}
                      alt={t.thumbnailAlt}
                      className={`w-12 h-12 rounded-lg border object-cover cursor-pointer transition-transform duration-200 group-hover/thumbnail:scale-110 ${isDark ? 'border-dark-600' : 'border-gray-300'}`}
                      onError={() => setThumbnailError(true)}
                    />
                    <div
                      className="fixed z-[9999] opacity-0 invisible group-hover/thumbnail:opacity-100 group-hover/thumbnail:visible transition-all duration-200 pointer-events-none"
                      style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                    >
                      <div className={`p-4 rounded-xl shadow-2xl border ${isDark ? 'bg-dark-800 border-dark-600' : 'bg-white border-gray-200'}`}>
                        {isLoadingHQ && !highQualityImage ? (
                          <div style={{ width: '320px', height: '320px' }} className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                          </div>
                        ) : (
                          <img
                            src={highQualityImage || qrThumbnail}
                            alt={t.expandedAlt}
                            style={{ width: '320px', height: '320px' }}
                            className="rounded-lg object-contain"
                          />
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-gray-100'}`}>
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>QR</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
              <div className="col-span-2 md:col-span-1">
                <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.columns.redirectUrl}</p>
                <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`} title={qrCode.redirect_url || qrCode.url}>
                  {QRCodeService.truncateUrl(qrCode.redirect_url || qrCode.url, 50)}
                </p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.columns.createdAt}</p>
                <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{QRCodeService.formatDate(qrCode.created_at)}</p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.columns.expiresAt}</p>
                <p className={`text-sm ${
                  qrCode.valid_until
                    ? (new Date(qrCode.valid_until) < new Date() ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-white' : 'text-gray-900'))
                    : (isDark ? 'text-gray-500' : 'text-gray-400')
                }`}>
                  {qrCode.valid_until ? QRCodeService.formatDate(qrCode.valid_until) : t.columns.unlimited}
                </p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.columns.scans}</p>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {stats?.total_visits || cachedStats?.total_visits || 0}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end">
              {showEditButton && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleEdit(); }}
                  className={`flex-shrink-0 p-2 transition-colors ${isDark ? 'text-gray-400 hover:text-primary-400' : 'text-gray-500 hover:text-primary-500'}`}
                  title={t.actions.edit}
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {showEditButton && onDownload && (
                <button
                  onClick={handleDownload}
                  className={`flex-shrink-0 p-2 transition-colors ${isDark ? 'text-gray-400 hover:text-green-400' : 'text-gray-500 hover:text-green-500'}`}
                  title={t.actions.download}
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              )}
              {showEditButton && onDelete && (
                <button
                  onClick={handleDelete}
                  className={`flex-shrink-0 p-2 transition-colors ${isDark ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'}`}
                  title={t.actions.delete}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleOpenLink(); }}
                className={`flex-shrink-0 p-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                title={t.actions.openLink}
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className={`border-t ${isDark ? 'border-dark-700 bg-dark-750' : 'border-gray-200 bg-gray-50'}`}>
          {isEditing ? (
            <QRCodeEditorInline qrCode={qrCode} onUpdate={handleUpdateQRCode} onCancel={handleCancelEdit} />
          ) : isLoadingStats ? (
            <div className="p-6 text-center">
              <div className={`inline-flex items-center space-x-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span>{t.loadingStats}</span>
              </div>
            </div>
          ) : statsError ? (
            <div className={`p-6 text-center ${isDark ? 'text-red-400' : 'text-red-500'}`}>{statsError}</div>
          ) : (
            <QRCodeStats stats={stats} qrCode={qrCode} />
          )}
        </div>
      )}
    </div>
  );
};

export default QRCodeRow;
